import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  defaultColDef,
  defaultGridOptionsFactory,
} from '@wilson/ag-grid/configuration';
import {
  Label,
  QualificationTrainings,
  RoleAction,
  RolePermissionSubject,
  User,
  UserQualificationV2,
  WhereOption,
} from '@wilson/interfaces';
import {
  FetchLabelsIfNeeded,
  LabelsListRendererComponent,
} from '@wilson/labels';
import { CustomInfiniteRowModelSelectAllHeaderComponent } from '@wilson/non-domain-specific/ag-grid-custom/ag-grid-select-all';
import {
  QualificationsListRendererComponent,
  UserQualificationsGateway,
} from '@wilson/qualification-v2/common';
import { EmptyStateComponent } from '@wilson/shared-components';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { isEqual } from 'lodash';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { TrainingQualificationsV2ListService } from '../../../service/training-qualifications-v2-list.service';
import {
  GetQualificationTrainingsOverview,
  ResetQualificationTrainingsOverviewDetails,
} from '../../../states/qualification-trainings-v2-overview/qualification-trainings-v2-overview.action';
import { TrainingQualificationsV2OverviewState } from '../../../states/qualification-trainings-v2-overview/qualification-trainings-v2-overview.state';
import { TrainingQualificationCreateService } from '../training-qualifications-create-drawer/training-qualifications-create.service';
import { QualificationAttachmentsRendererComponent } from './renderers/qualification-training-attachments-renderer.component';
import { QualificationTrainingProviderRendererComponent } from './renderers/qualification-training-provider-renderer.component';
import { QualificationTrainingStatusRendererComponent } from './renderers/qualification-training-status-renderer.component';
import { QualificationTrainingUserRendererComponent } from './renderers/qualification-training-user-renderer';

@Component({
  selector: 'wilson-qualification-trainings-v2-list',
  templateUrl: './qualification-trainings-v2-list.component.html',
  styleUrls: ['./qualification-trainings-v2-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserQualificationsGateway],
  imports: [AgGridAngular, AgGridModule, NzDrawerModule],
})
export class QualificationTrainingsV2ListComponent
  implements OnInit, OnDestroy
{
  private readonly translateService = inject(TranslateService);
  private store = inject(Store);
  private readonly router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private readonly trainingQualificationsV2ListService = inject(
    TrainingQualificationsV2ListService,
  );
  private readonly trainingQualificationCreateService = inject(
    TrainingQualificationCreateService,
  );
  @Input() searchFilter!: BehaviorSubject<string>;
  @Input() statusFilter!: BehaviorSubject<string[]>;
  @Input() usersFilter!: BehaviorSubject<string[]>;
  @Input() qualificationFilter!: BehaviorSubject<string[]>;
  @Input() refreshSubject!: BehaviorSubject<number>;
  @Output() selectedRowsChange = new EventEmitter<QualificationTrainings[]>();

  public can = inject(AbilityServiceSignal).can;
  public readonly RoleAction = RoleAction;
  public readonly RolePermissionSubject = RolePermissionSubject;

  private destroy$ = new Subject<void>();

  private readonly defaultSortingFields: Record<string, 'asc' | 'desc'> | null =
    { userName: 'asc' };
  private filters = {
    statuses: [] as string[],
    search: '',
    lang: '',
    userIds: [] as string[],
    qualificationIds: [] as string[],
  };

  pageSize = this.store.selectSnapshot(
    TrainingQualificationsV2OverviewState.getPageSize,
  );

  public readonly defaultColDef = defaultColDef;
  readonly gridOptions: GridOptions = {
    ...defaultGridOptionsFactory(),
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    rowModelType: 'infinite',
    cacheBlockSize: this.pageSize,
    paginationPageSize: this.pageSize,
    pagination: true,
    paginationPageSizeSelector: [50, 100, 150],
    suppressPaginationPanel: false,
    paginationAutoPageSize: false,
    onSortChanged: (params) => this.handleSortChanged(params),
    multiSortKey: 'ctrl',
    loading: true,
  };

  isDrawerVisible = false;
  selectedQualification!: UserQualificationV2;
  employee!: User;

  public readonly components = {
    selectAllHeaderComponent: CustomInfiniteRowModelSelectAllHeaderComponent,
    noRowsOverlay: EmptyStateComponent,
    qualificationsListRendererComponent: QualificationsListRendererComponent,
    labelsListRendererComponent: LabelsListRendererComponent,
    qualificationTrainingStatusRendererComponent:
      QualificationTrainingStatusRendererComponent,
    qualificationTrainingUserRendererComponent:
      QualificationTrainingUserRendererComponent,
    qualificationTrainingProviderRendererComponent:
      QualificationTrainingProviderRendererComponent,
    qualificationAttachmentsRendererComponent:
      QualificationAttachmentsRendererComponent,
  };

  private gridApi!: GridApi;
  protected columnDefs: ColDef[] = [
    {
      resizable: false,
      sortable: false,
      width: 50,
      checkboxSelection: true,
      headerComponent: 'selectAllHeaderComponent',
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
    },
    {
      flex: 1,
      resizable: true,
      field: 'name',
      headerName: this.translateService.instant(
        'page.training-qualifications.name',
      ),
      sortable: true,
      sort: 'asc',
      colId: 'name',
    },
    {
      flex: 1,
      resizable: true,
      field: 'user.firstName',
      cellRenderer: 'qualificationTrainingUserRendererComponent',
      headerName: this.translateService.instant('page.qualifications.user'),
      sortable: true,
      colId: 'userName',
    },
    {
      flex: 1,
      resizable: true,
      field: 'qualifications',
      cellRenderer: 'qualificationsListRendererComponent',
      cellRendererParams: { maxVisible: 1 },
      headerName: this.translateService.instant(
        'page.training-qualifications.qualifications',
      ),
      sortable: false,
      colId: 'qualificationTraining',
    },
    {
      flex: 1,
      field: 'status',
      cellRenderer: 'qualificationTrainingStatusRendererComponent',
      headerName: this.translateService.instant('general.status'),
      sortable: true,
      colId: 'status',
    },
    {
      flex: 1,
      resizable: true,
      field:
        'organizationalUnitQualificationTrainingProvider.qualificationTrainingProvider.name',
      cellRenderer: 'qualificationTrainingProviderRendererComponent',
      headerName: this.translateService.instant(
        'page.training-qualifications.provider',
      ),
      sortable: true,
      colId: 'qualificationTrainingProviderName',
    },
    {
      flex: 1,
      resizable: true,
      field: 'labels',
      cellRenderer: 'labelsListRendererComponent',
      cellRendererParams: { maxVisible: 1 },
      valueGetter: (params) =>
        params.data?.labels?.map((l: Pick<Label, 'id' | 'name'>) => l.id),
      headerName: this.translateService.instant(
        'page.training-qualifications.labels',
      ),
      sortable: false,
      colId: 'labels',
    },
    {
      flex: 1,
      resizable: true,
      field: 'qualificationTrainingAttachments',
      cellRenderer: 'qualificationAttachmentsRendererComponent',
      headerName: this.translateService.instant(
        'page.training-qualifications.documents',
      ),
      sortable: false,
      colId: 'attachments',
    },
  ];
  protected showGrid = false;
  public paginationChangedListener = (): void => this.handlePaginationChanged();

  qualificationsTrainings$ = this.store.select(
    TrainingQualificationsV2OverviewState.getQualificationTrainings,
  );
  isLoading$ = this.store.select(
    TrainingQualificationsV2OverviewState.isLoading,
  );
  isDrawerVisible$ = this.store.select(
    TrainingQualificationsV2OverviewState.isDrawerVisible,
  );
  qualificationTrainingDetails$ = this.store.select(
    TrainingQualificationsV2OverviewState.getQualificationTrainingDetails,
  );

  ngOnInit(): void {
    this.store.dispatch(new FetchLabelsIfNeeded());

    combineLatest([
      this.searchFilter,
      this.translateService.onLangChange.pipe(
        startWith({ lang: this.translateService.getCurrentLang() ?? '' }),
        map((event) => ({ lang: event.lang.split('-')[0] })),
      ),
      this.statusFilter,
      this.usersFilter,
      this.qualificationFilter,
      this.refreshSubject,
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap(([search, langEvent, statuses, userIds, qualificationIds]) => {
          this.filters = {
            search,
            lang: langEvent.lang,
            statuses,
            userIds,
            qualificationIds,
          };
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.gridApi) {
          this.refreshGrid();
          this.store.dispatch(new ResetQualificationTrainingsOverviewDetails());
          this.trainingQualificationsV2ListService.clearSelection();
        }
      });

    this.trainingQualificationCreateService.trainingQualificationCreatedQualifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.gridApi) {
          this.refreshGrid();
        }
      });
  }

  ngOnDestroy(): void {
    this.gridApi.removeEventListener(
      'paginationChanged',
      this.paginationChangedListener,
    );
    this.store.dispatch(new ResetQualificationTrainingsOverviewDetails());
    this.trainingQualificationsV2ListService.clearSelection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridReady(params: { api: GridApi }): void {
    this.gridApi = params.api;
    this.gridApi.removeEventListener(
      'paginationChanged',
      this.paginationChangedListener,
    );
    this.gridApi.setGridOption(
      'datasource',
      this.createDataSource(this.defaultSortingFields),
    );
    this.gridApi.addEventListener('selectionChanged', () => {
      const selectedRows = this.gridApi.getSelectedRows();
      this.selectedRowsChange.emit(selectedRows);
    });
  }

  public onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedRows = event.api.getSelectedRows();
    this.trainingQualificationsV2ListService.setSelectedQualificationTrainings(
      selectedRows,
    );
  }

  private createDataSource(
    sortFields?: Record<string, 'asc' | 'desc'> | null,
  ): {
    getRows: (params: IGetRowsParams) => void;
  } {
    return {
      getRows: (params: IGetRowsParams): void => {
        this.gridApi.removeEventListener(
          'paginationChanged',
          this.paginationChangedListener,
        );

        let pageSize = this.gridApi.paginationGetPageSize();
        const pageNumber = Math.floor(params.startRow / pageSize) + 1;

        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (queryParams['pageSize']) {
          pageSize = queryParams['pageSize'] as number;
        }

        this.gridApi.updateGridOptions({
          cacheBlockSize: pageSize,
          paginationPageSize: pageSize,
        });

        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {
            pageSize,
          },
          queryParamsHandling: 'merge',
        });

        if (pageNumber < 1) {
          params.failCallback();
          return;
        }

        this.gridApi.setGridOption('loading', true);
        this.showGrid = false;

        const whereParams = this.setWhereOptions(sortFields);

        this.store
          .dispatch(
            new GetQualificationTrainingsOverview({
              pageNumber,
              pageSize,
              relations: ['qualificationTrainingRouteRecord'],
              lang: this.filters.lang,
              ...whereParams,
            }),
          )
          .pipe(
            catchError(() => {
              params.failCallback();
              return of({ data: [], pagination: { totalRecords: 0 } });
            }),
            finalize(() => {
              this.gridApi.setGridOption('loading', false);
              this.showGrid = true;
              this.gridApi.addEventListener(
                'paginationChanged',
                this.paginationChangedListener,
              );
            }),
            switchMap(() =>
              combineLatest([
                this.store.select(
                  TrainingQualificationsV2OverviewState.getQualificationTrainings,
                ),
                this.store.select(
                  TrainingQualificationsV2OverviewState.getTotalRecords,
                ),
              ]),
            ),
            take(1),
          )
          .subscribe(([qualificationTrainings, totalRecords]) => {
            if (qualificationTrainings?.length) {
              params.successCallback(qualificationTrainings, totalRecords);
            } else {
              params.successCallback([], 0);
              this.gridApi.setGridOption('loading', false);
              this.gridApi.showNoRowsOverlay();
            }
          });
      },
    };
  }

  private setWhereOptions(
    sortFields?: Record<string, 'asc' | 'desc'> | null,
  ): WhereOption {
    let whereOptions = {};

    if (this.filters.search) {
      whereOptions = {
        ...whereOptions,
        searchText: this.filters.search,
      };
    }

    if (this.filters.statuses?.length) {
      whereOptions = {
        ...whereOptions,
        statuses: this.filters.statuses,
      };
    }

    if (this.filters.userIds?.length) {
      whereOptions = {
        ...whereOptions,
        userIds: this.filters.userIds,
      };
    }

    if (this.filters.qualificationIds?.length) {
      whereOptions = {
        ...whereOptions,
        qualificationIds: this.filters.qualificationIds,
      };
    }

    if (sortFields && Object.keys(sortFields).length > 0) {
      whereOptions = {
        ...whereOptions,
        order: sortFields,
      };
    }

    whereOptions = {
      ...whereOptions,
      lang: this.filters.lang,
    };

    return whereOptions;
  }

  private handleSortChanged(params: { api: GridApi }): void {
    const sortModel = params.api.getColumnState();
    const sortFields = sortModel
      .filter((sort) => sort.sort)
      .reduce(
        (acc, sort) => ({
          ...acc,
          [sort.colId]: sort.sort?.toLowerCase(),
        }),
        {},
      );

    this.refreshGridWithSort(sortFields);
  }

  private refreshGridWithSort(
    sortFields: Record<string, 'asc' | 'desc'> | null,
  ): void {
    if (this.gridApi) {
      this.gridApi.removeEventListener(
        'paginationChanged',
        this.paginationChangedListener,
      );
      const dataSource = this.createDataSource(sortFields);
      this.gridApi.setGridOption('datasource', dataSource);
    }
  }

  private handlePaginationChanged(): void {
    const pageSize = this.gridApi.paginationGetPageSize();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        pageSize,
      },
      queryParamsHandling: 'merge',
    });

    this.gridApi.setGridOption('cacheBlockSize', pageSize);
  }

  private refreshGrid(): void {
    if (this.gridApi) {
      this.gridApi.removeEventListener(
        'paginationChanged',
        this.paginationChangedListener,
      );
      const dataSource = this.createDataSource(this.defaultSortingFields);
      this.gridApi.setGridOption('datasource', dataSource);
    }
  }
}
